// Disposable Tenant Admin API test UI for Data Connections.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiHttpError, type ApiConfig, getDataHealthPipeline, listLogs } from "./api";
import { formatTenant } from "./format";
import { AlarmsPanel } from "./components/AlarmsPanel";
import { InternalLineagePanel } from "./components/InternalLineagePanel";
import { ConfigPanel } from "./components/ConfigPanel";
import { DrillDown } from "./components/DrillDown";
import { FilterBar } from "./components/FilterBar";
import { IntegrationsPanel } from "./components/IntegrationsPanel";
import { LogRow } from "./components/LogRow";
import { OverviewPanel } from "./components/OverviewPanel";
import { PipelinePanel } from "./components/PipelinePanel";
import { WinbackPanel } from "./components/WinbackPanel";
import type { LevelFilter, PipelineCadence, PipelineEvent } from "./types";

type View =
  | "overview"
  | "integrations"
  | "pipeline"
  | "modelgraph"
  | "logs"
  | "winback"
  | "alarms";

const CONFIG_KEY = "growdhi.tenantAdminApi.config";
const POLL_INTERVAL_MS = 3000;
const PAGE_SIZE = 100;

function loadConfig(): ApiConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.baseUrl && parsed.apiKey && parsed.tenantId) return parsed as ApiConfig;
  } catch {
    /* fall through */
  }
  return null;
}

function saveConfig(cfg: ApiConfig): void {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
}

export function App() {
  const [config, setConfig] = useState<ApiConfig | null>(loadConfig);
  const [view, setView] = useState<View>("overview");
  const [events, setEvents] = useState<PipelineEvent[]>([]);
  const [level, setLevel] = useState<LevelFilter>("all");
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
  const [live, setLive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drillDown, setDrillDown] = useState<PipelineEvent | null>(null);
  const [cadenceBySource, setCadenceBySource] = useState<Record<string, PipelineCadence>>({});
  const lastTsRef = useRef<string | null>(null);

  const fetchAll = useCallback(async (): Promise<void> => {
    if (!config || view !== "logs") return;
    try {
      const resp = await listLogs(config, { level, limit: PAGE_SIZE });
      setEvents(resp.data);
      setError(null);
      lastTsRef.current = resp.data[0]?.ts_utc ?? null;
    } catch (e) {
      setError(formatError(e));
    }
  }, [config, level, view]);

  const fetchSince = useCallback(async (): Promise<void> => {
    if (!config || view !== "logs" || !lastTsRef.current) return;
    try {
      const resp = await listLogs(config, {
        level,
        since: lastTsRef.current,
        limit: PAGE_SIZE,
      });
      if (resp.data.length > 0) {
        setEvents((prev) => {
          const seen = new Set(prev.map((e) => e.event_id));
          const fresh = resp.data.filter((e) => !seen.has(e.event_id));
          return [...fresh, ...prev].slice(0, 500);
        });
        lastTsRef.current = resp.data[0].ts_utc;
      }
      setError(null);
    } catch (e) {
      setError(formatError(e));
    }
  }, [config, level, view]);

  useEffect(() => {
    if (config) fetchAll();
  }, [config, fetchAll, level]);

  useEffect(() => {
    if (!config || !live || view !== "logs") return;
    const id = setInterval(() => {
      void fetchSince();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [config, fetchSince, live, view]);

  // Fetch each source's live delivery cadence once when the Logs tab opens so
  // the per-row cadence chip reflects the actual schedule. Best-effort: on
  // failure the chip falls back to the pipeline-slug heuristic.
  useEffect(() => {
    if (!config || view !== "logs") return;
    let cancelled = false;
    getDataHealthPipeline(config)
      .then((resp) => {
        if (!cancelled) setCadenceBySource(resp.meta.cadence_by_source ?? {});
      })
      .catch(() => {
        /* best-effort - LogRow falls back to the slug heuristic */
      });
    return () => {
      cancelled = true;
    };
  }, [config, view]);

  const visible = useMemo(() => {
    if (selectedSources.size === 0) return events;
    return events.filter((e) => selectedSources.has(e.source_system));
  }, [events, selectedSources]);

  const sources = useMemo(() => {
    const s = new Set(events.map((e) => e.source_system));
    return Array.from(s).sort();
  }, [events]);

  if (!config) {
    return (
      <ConfigPanel
        initial={null}
        onSave={(cfg) => {
          saveConfig(cfg);
          setConfig(cfg);
        }}
      />
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          Growdhi <strong>Data Connections</strong>{" "}
          <span className="tenant-chip">tenant: {formatTenant(config.tenantId)}</span>
        </div>
        <nav className="topnav" aria-label="Data Connections views">
          <button
            className={view === "overview" ? "active" : ""}
            onClick={() => setView("overview")}
          >
            Overview
          </button>
          <button
            className={view === "integrations" ? "active" : ""}
            onClick={() => setView("integrations")}
          >
            Integrations
          </button>
          <button
            className={view === "pipeline" ? "active" : ""}
            onClick={() => setView("pipeline")}
          >
            Data Health
          </button>
          <button
            className={view === "winback" ? "active" : ""}
            onClick={() => setView("winback")}
          >
            Winback
          </button>
          <button
            className={view === "modelgraph" ? "active" : ""}
            onClick={() => setView("modelgraph")}
          >
            Model graph
          </button>
          <button
            className={view === "alarms" ? "active" : ""}
            onClick={() => setView("alarms")}
          >
            Alarms
          </button>
          <button className={view === "logs" ? "active" : ""} onClick={() => setView("logs")}>
            Logs
          </button>
        </nav>
        <button
          className="settings-btn"
          onClick={() => {
            localStorage.removeItem(CONFIG_KEY);
            setConfig(null);
          }}
          title="Reset API URL / key"
        >
          Settings
        </button>
      </header>

      {view === "overview" && (
        <OverviewPanel config={config} onNavigate={(target) => setView(target)} />
      )}

      {view === "logs" && (
        <>
          <FilterBar
            level={level}
            onLevelChange={setLevel}
            live={live}
            onLiveToggle={setLive}
            sources={sources}
            selectedSources={selectedSources}
            onSelectedSourcesChange={setSelectedSources}
            eventCount={visible.length}
          />

          {error && <div className="error-banner">{error}</div>}

          <main className="rows">
            {visible.length === 0 && !error && (
              <div className="empty">
                No events yet. The ingester writes a row each time a Step Functions
                execution starts, succeeds, or fails.
              </div>
            )}
            {visible.map((e) => (
              <LogRow
                key={e.event_id}
                event={e}
                cadenceBySource={cadenceBySource}
                onClick={() => setDrillDown(e)}
              />
            ))}
          </main>

          {drillDown && <DrillDown event={drillDown} onClose={() => setDrillDown(null)} />}
        </>
      )}

      {view === "integrations" && <IntegrationsPanel config={config} />}

      {view === "pipeline" && <PipelinePanel config={config} />}

      {view === "modelgraph" && <InternalLineagePanel config={config} />}

      {view === "winback" && <WinbackPanel config={config} />}

      {view === "alarms" && <AlarmsPanel config={config} />}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiHttpError) {
    return `${e.status} ${e.code}: ${e.message}${e.requestId ? ` (req ${e.requestId})` : ""}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
